// @flow

import { defaultStackParser } from '@sentry/react'

import { byteLength, limitText } from './fields'
import { compressMessage, getErrorType } from './errorInfo'
import {
  CRASH_CODE,
  FIELD_LIMITS,
  formatRelease,
  formatPlatform,
  formatDevice,
  type CrashInfo,
} from './serviceLine'

export const PAYLOAD_VERSION = 1
export const PAYLOAD_MAX_BYTES = 800

const DEVICE_ID_MAX_BYTES = 64
const BASE_MAX_BYTES = 64
const FRAME_NAME_MAX_BYTES = 40
const MAX_STACK_FRAMES = 15
const MAX_COMPONENT_FRAMES = 8

export type PayloadData = CrashInfo & {
  deviceId: string,
  error: mixed,
  componentStack: ?string,
}

type Frame = {
  name: string,
  // "file:line"; empty for frames without a position, like "at div" in a component stack
  base: string,
  col: string,
}

const stripUrlPrefix = (url: string): string => {
  const index = url.lastIndexOf('static/js/')

  return index >= 0 ? url.slice(index + 'static/js/'.length) : url.slice(url.lastIndexOf('/') + 1)
}

// Sentry returns the oldest frame first and puts the name of a frame without
// a position into `filename`
// eslint-disable-next-line complexity
const toFrame = (frame: Object): Frame => {
  const name = frame.function && frame.function !== '?' ? frame.function : ''

  if (!frame.filename || frame.lineno == null || frame.colno == null) {
    return { name: name || frame.filename || '', base: '', col: '' }
  }

  return {
    name,
    base: `${stripUrlPrefix(frame.filename)}:${frame.lineno}`,
    col: String(frame.colno),
  }
}

export const parseFrames = (stack: ?string, limit: number): Array<Frame> => {
  if (typeof stack !== 'string' || !stack) return []

  return defaultStackParser(stack).reverse().slice(0, limit).map(toFrame)
}

// The most frequent "file:line" is written once as "f", ties go to the first seen
export const pickBase = (frames: Array<Frame>): string => {
  const counts = {}
  let best = ''

  // eslint-disable-next-line complexity
  frames.forEach(({ base }) => {
    if (!base) return

    counts[base] = (counts[base] || 0) + 1

    if (!best || counts[base] > counts[best]) best = base
  })

  return byteLength(best) <= BASE_MAX_BYTES ? best : ''
}

// Only the name is shortened: file, line and column are always kept in full
// eslint-disable-next-line complexity
export const formatFrame = (frame: Frame, base: string): string => {
  const name = limitText(frame.name, FRAME_NAME_MAX_BYTES) || '?'

  if (!frame.base) return name
  if (frame.base === base) return `${name}:${frame.col}`

  return `${name}@${frame.base}:${frame.col}`
}

const buildHeader = (data: PayloadData) => ({
  v: PAYLOAD_VERSION,
  code: CRASH_CODE,
  t: limitText(getErrorType(data.error), FIELD_LIMITS.errorType),
  id: limitText(data.eventId, FIELD_LIMITS.eventId),
  st: limitText(data.sendStatus, FIELD_LIMITS.sendStatus),
  tag: limitText(data.tag, FIELD_LIMITS.tag),
  rel: formatRelease(data),
  os: formatPlatform(data),
  dev: formatDevice(data),
  did: limitText(data.deviceId, DEVICE_ID_MAX_BYTES),
  uid: limitText(data.customerId, FIELD_LIMITS.customerId),
  ts: new Date(data.time).toISOString(),
  m: compressMessage(data.error),
})

// Returns null when the QR cannot be built: the mandatory fields alone do not fit,
// or a library failed. The crash screen then shows everything except the QR.
// eslint-disable-next-line complexity
export const buildPayload = (data: PayloadData): ?string => {
  try {
    const { error }: { error: any } = data
    const header = buildHeader(data)
    const stackFrames = parseFrames(error && error.stack, MAX_STACK_FRAMES)
    const componentFrames = parseFrames(data.componentStack, MAX_COMPONENT_FRAMES)
    const base = pickBase(componentFrames.concat(stackFrames))

    const serialize = () => {
      const usesBase = stackFrames.concat(componentFrames).some((frame) => frame.base === base)

      return JSON.stringify({
        ...header,
        f: usesBase ? base : '',
        s: stackFrames.map((frame) => formatFrame(frame, base)),
        c: componentFrames.map((frame) => formatFrame(frame, base)),
      })
    }

    let json = serialize()

    // Stack frames are the least useful, so they go first; then component frames
    while (byteLength(json) > PAYLOAD_MAX_BYTES) {
      if (stackFrames.length) stackFrames.pop()
      else if (componentFrames.length) componentFrames.pop()
      else return null

      json = serialize()
    }

    return json
  } catch (e) {
    return null
  }
}
